import * as React from 'react';
import {
  isPopoverTarget,
  isPopoverAction,
  type PopoverAction,
} from '@packages/systems/overlays';
import {
  Note,
  SegmentedControl,
  SegmentedControlButton,
} from '@packages/systems/spring';
import {useExpressionEditorContext} from '@packages/systems/ExpressionEditor-utils';
import {SectionSelectorPopover} from '@packages/systems/designer/section-select';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {Label} from './LinkUIComponents';
import type {State} from './LinkTypes';
import {emotionStyled} from '@packages/utilities/styled';
import {getScopedOverlayTargetComponent} from './LinkOverlayTargets';
import {
  selectOverlayContext,
  useOverlayTargetCreation,
  useGetContainingOverlayTarget,
  useOverlayTargetComponent,
  useOverlayTargetOptions,
} from './LinkOverlayTargetHooks';

const FullWidthNote = emotionStyled(Note)({gridColumn: '1 / -1'});

// Compact inline padding lets all three actions fit the narrower Link field
// column in the conditionals menu; wider columns still stretch each button.
const PopoverActionSegmentedControl = emotionStyled(SegmentedControl)({
  '& > button': {
    paddingLeft: '$1',
    paddingRight: '$1',
  },
});

const getPopoverAction = (action: State['popoverAction']): PopoverAction =>
  isPopoverAction(action) ? action : 'toggle';

/**
 * Reads the designer state only when called, so Link settings outside Popover
 * mode don't subscribe to every render change.
 */
export const useGetContainingPopoverTarget = (): (() => Pick<
  State,
  'popoverTarget' | 'popoverTargetComponent'
> | null) => {
  const getContainingTarget = useGetContainingOverlayTarget(isPopoverTarget);

  return React.useCallback(() => {
    const target = getContainingTarget();
    return target
      ? {
          popoverTarget: target.targetId,
          popoverTargetComponent: target.targetComponent,
        }
      : null;
  }, [getContainingTarget]);
};

export const LinkPopoverMode = ({
  state,
  dispatch,
  isOverride,
}: {
  state: State;
  dispatch: (arg1?: any) => void;
  isOverride: boolean;
}) => {
  const selectedId = state.popoverTarget;
  const isEditingDefault = useExpressionEditorContext() === 'field-editor';
  const currentPopoverTargetComponent = useOverlayTargetComponent();
  const overlayContext = useDesignerSelector(selectOverlayContext);
  const {render, trigger, isCmsRichTextScope, targetCandidates} =
    overlayContext;
  const popoverTargetComponent = getScopedOverlayTargetComponent(
    currentPopoverTargetComponent,
    isCmsRichTextScope
  );
  const onCreate = useOverlayTargetCreation(
    'popover',
    popoverTargetComponent,
    overlayContext,
    isOverride
  );
  const {options, hasValidOptions} = useOverlayTargetOptions({
    isTarget: isPopoverTarget,
    missingLabel: 'Missing popover',
    selectedId,
    render,
    selectedElement: trigger,
    isEditingDefault,
    candidates: targetCandidates,
  });
  const onActionChange = React.useCallback(
    (action: string) => {
      if (!isPopoverAction(action)) {
        return;
      }

      dispatch({
        payload: {...state, popoverAction: action},
      });
    },
    [dispatch, state]
  );

  const onChange = React.useCallback(
    (value: string | null) => {
      dispatch({
        payload: {
          ...state,
          popoverTarget: value ?? undefined,
          popoverTargetComponent,
        },
      });
    },
    [dispatch, popoverTargetComponent, state]
  );

  return (
    <>
      {!isEditingDefault || selectedId ? (
        <>
          <Label
            data-automation-id="symbols-dataType-popoverTarget"
            isOverride={isOverride}
          >
            Target
          </Label>
          <SectionSelectorPopover
            value={selectedId}
            onSectionChange={onChange}
            options={options}
            placeholder="Choose a target..."
            searchPlaceholder="Search targets"
            emptyStateLabel="No popovers found"
            hasValidOptions={hasValidOptions}
            emptyAction={
              onCreate
                ? {
                    label: 'Create popover',
                    onClick: onCreate,
                  }
                : undefined
            }
          />
        </>
      ) : null}
      {isEditingDefault ? (
        <FullWidthNote colorVariant="default" hasIcon={false}>
          Set the popover target on the component instance.
        </FullWidthNote>
      ) : null}
      <Label
        data-automation-id="symbols-dataType-popoverAction"
        isOverride={isOverride}
      >
        Action
      </Label>
      <PopoverActionSegmentedControl
        data-automation-id="Type--Plugin_Enum_PopoverAction_buttons"
        onChange={onActionChange}
        value={getPopoverAction(state.popoverAction)}
      >
        <SegmentedControlButton
          data-automation-id="Type--Plugin_Enum_PopoverAction_button-toggle"
          value="toggle"
        >
          Toggle
        </SegmentedControlButton>
        <SegmentedControlButton
          data-automation-id="Type--Plugin_Enum_PopoverAction_button-show"
          value="show"
        >
          Show
        </SegmentedControlButton>
        <SegmentedControlButton
          data-automation-id="Type--Plugin_Enum_PopoverAction_button-hide"
          value="hide"
        >
          Hide
        </SegmentedControlButton>
      </PopoverActionSegmentedControl>
    </>
  );
};
